/**
 * WB4.7 Phase C — object revision API client (durable schema v1).
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type { WorkbenchRevisionSnapshotRecord } from '@shared/business-workbench/workbench-revision-snapshot';
import type { WorkbenchObjectRevisionRecord } from '@shared/business-workbench/workbench-object-revision';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { fetchWithAuth } from './authFetch';

const getBaseUrl = (): string => resolveApiOrigin();

interface ObjectRevisionListEnvelope {
  success?: boolean;
  data?: {
    revisions?: WorkbenchObjectRevisionRecord[];
  };
}

interface ObjectRevisionRestoreEnvelope {
  success?: boolean;
  data?: {
    revision?: WorkbenchRevisionSnapshotRecord | null;
  };
}

export async function fetchWorkbenchObjectRevisionsRemote(input: {
  tenantId: string;
  objectId: string;
  limit?: number;
  includeRestored?: boolean;
}): Promise<WorkbenchObjectRevisionRecord[]> {
  const normalizedObjectId = input.objectId.trim();
  if (!normalizedObjectId) {
    return [];
  }
  try {
    const params = new URLSearchParams({ objectId: normalizedObjectId });
    if (input.limit !== undefined) {
      params.set('limit', String(input.limit));
    }
    if (input.includeRestored) {
      params.set('includeRestored', 'true');
    }
    const url = `${getBaseUrl()}/api/workspace/workbench-object-revisions?${params.toString()}`;
    const res = await fetchWithAuth(url, {
      method: 'GET',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as ObjectRevisionListEnvelope;
    if (!res.ok || body.success === false) {
      return [];
    }
    return body.data?.revisions ?? [];
  } catch {
    return [];
  }
}

export async function restoreWorkbenchObjectRevisionRemote(input: {
  tenantId: string;
  revisionId: string;
}): Promise<WorkbenchRevisionSnapshotRecord | null> {
  const revisionId = input.revisionId.trim();
  if (!revisionId) {
    return null;
  }
  try {
    const url = `${getBaseUrl()}/api/workspace/workbench-object-revisions/${encodeURIComponent(revisionId)}/restore`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as ObjectRevisionRestoreEnvelope;
    if (!res.ok || body.success === false || !body.data?.revision) {
      return null;
    }
    return body.data.revision;
  } catch {
    return null;
  }
}
